import { BookOpen, CalendarDays, ClipboardCheck, FileSpreadsheet, History, LayoutDashboard, ListOrdered, MessageSquareWarning, ShoppingCart, TrendingUp, Truck, Users, Wine, type LucideIcon } from 'lucide-react';
import { can, type Capability } from '@/domain/permissions';
import type { Role } from '@/domain/types';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Required capability; omitted = every manager. */
  cap?: Capability;
  end?: boolean;
  /** Shows how many nights are waiting for a manager. */
  badge?: 'nights';
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Two parts: the staff screens (a few big buttons, no menus) and the manager area under
 * /admin. The guide (/help) is open to everyone.
 */
export function isManagerPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

export const NAV: NavGroup[] = [
  {
    label: 'Every day',
    items: [
      { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
      { to: '/admin/nights', label: 'Check & approve nights', icon: ClipboardCheck, cap: 'count.approve', badge: 'nights' },
      { to: '/admin/moves', label: 'Deliveries & transfers', icon: Truck },
      { to: '/admin/week', label: 'Week & Excel file', icon: CalendarDays, cap: 'reports.view' },
    ],
  },
  {
    label: 'Planning',
    items: [
      { to: '/admin/order', label: 'Beer order', icon: ShoppingCart, cap: 'items.edit' },
      { to: '/admin/trends', label: 'Sales trends', icon: TrendingUp, cap: 'reports.view' },
      { to: '/admin/specials', label: 'Drink costs & specials', icon: Wine, cap: 'reports.view' },
    ],
  },
  {
    label: 'Setup',
    items: [
      { to: '/admin/items', label: 'Drink lists', icon: ListOrdered, cap: 'items.edit' },
      { to: '/admin/people', label: 'People & PINs', icon: Users },
      { to: '/admin/data', label: 'Excel files & settings', icon: FileSpreadsheet },
      { to: '/admin/history', label: 'History of changes', icon: History },
      { to: '/admin/feedback', label: 'Feedback', icon: MessageSquareWarning },
      { to: '/help', label: 'How to use the app', icon: BookOpen },
    ],
  },
];

export function navFor(role: Role): NavGroup[] {
  return NAV.map((g) => ({ ...g, items: g.items.filter((i) => !i.cap || can(role, i.cap)) })).filter((g) => g.items.length > 0);
}

/** The four most-used manager places for the phone bottom bar (the fifth slot is "Menu"). */
export const MANAGER_BOTTOM_NAV: NavItem[] = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/nights', label: 'Nights', icon: ClipboardCheck, badge: 'nights' },
  { to: '/admin/week', label: 'Excel', icon: CalendarDays },
  { to: '/admin/trends', label: 'Trends', icon: TrendingUp },
];
